
import { Union } from "../fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type } from "../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { equals, fromParts, compare, max, floor, op_Division, op_Multiply, min } from "../fable_modules/fable-library-js.5.3.0/Decimal.js";
import Decimal from "../fable_modules/fable-library-js.5.3.0/Decimal.js";
import { FSharpResult$2 } from "../fable_modules/fable-library-js.5.3.0/Result.js";
import { printf, toText } from "../fable_modules/fable-library-js.5.3.0/String.js";

/**
 * Pledge lifecycle status, derived from boolean flags in view DB
 */
export class PledgeStatus extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Active", "Cancelled", "Paid", "Abandoned", "NoDonationDue"];
    }
}

export function PledgeStatus_$reflection() {
    return union_type("Shared.PledgeStatus", [], PledgeStatus, () => [[], [], [], [], []]);
}

/**
 * Convert view DB boolean flags to status. Priority: Paid > NoDonationDue > Cancelled > Abandoned > Active
 */
export function PledgeStatusModule_fromFlags(isCancelled, isPaid, isAbandoned, isNoDonationDue) {
    if (isPaid) {
        return new PledgeStatus(2, []);
    }
    else if (isNoDonationDue) {
        return new PledgeStatus(4, []);
    }
    else if (isCancelled) {
        return new PledgeStatus(1, []);
    }
    else if (isAbandoned) {
        return new PledgeStatus(3, []);
    }
    else {
        return new PledgeStatus(0, []);
    }
}

/**
 * Convert status to string for API responses
 */
export function PledgeStatusModule_toString(_arg) {
    switch (_arg.tag) {
        case 1:
            return "cancelled";
        case 2:
            return "paid";
        case 3:
            return "abandoned";
        case 4:
            return "no_donation_due";
        default:
            return "active";
    }
}

/**
 * Parse from string (for deserialization)
 */
export function PledgeStatusModule_fromString(_arg) {
    switch (_arg) {
        case "active":
            return new PledgeStatus(0, []);
        case "cancelled":
            return new PledgeStatus(1, []);
        case "paid":
            return new PledgeStatus(2, []);
        case "abandoned":
            return new PledgeStatus(3, []);
        case "no_donation_due":
            return new PledgeStatus(4, []);
        default:
            return undefined;
    }
}

/**
 * The units a pledge's donation is calculated against.
 * If capped: min(unitsCompleted, goalUnits)
 * If uncapped: unitsCompleted
 */
export function PledgeCalculation_cappedUnits(goalUnits, unitsCompleted, capAtGoal) {
    if (capAtGoal) {
        return min(unitsCompleted, goalUnits);
    }
    else {
        return unitsCompleted;
    }
}

/**
 * Calculate the donation estimate for a pledge.
 * If capped: amountPerUnit * min(unitsCompleted, goalUnits)
 * If uncapped: amountPerUnit * unitsCompleted
 */
export function PledgeCalculation_calculateDonationEstimate(amountPerUnit, goalUnits, unitsCompleted, capAtGoal) {
    return op_Multiply(amountPerUnit, PledgeCalculation_cappedUnits(goalUnits, unitsCompleted, capAtGoal));
}

/**
 * Label for the donation estimate display
 */
export function PledgeCalculation_estimateLabel(capAtGoal) {
    if (capAtGoal) {
        return "Goal";
    }
    else {
        return "So far";
    }
}

/**
 * Floor a decimal to N decimal places (truncate toward zero, no banker's rounding).
 * Used by the two-box pledge form to enforce the rounding contract:
 * per-unit truncated to 4dp, total truncated to 2dp.
 */
export function PledgeCalculation_floorTo(places, x) {
    const factor = new Decimal(Math.pow(10, places));
    return op_Division(floor(op_Multiply(x, factor)), factor);
}

/**
 * Denominator used by the two-box pledge form.
 * Uncapped: max(goalUnits, unitsCompleted), so the total follows actual progress past the goal.
 * Capped: goalUnits, because the cap anchors the total to the goal.
 */
export function PledgeCalculation_denominator(goalUnits, unitsCompleted, capAtGoal) {
    if (capAtGoal) {
        return goalUnits;
    }
    else {
        return max(goalUnits, unitsCompleted);
    }
}

/**
 * Back-solve per-unit from a typed total. Returns the 4dp value to store.
 * Returns 0m when denominator is 0m (defensive guard — never happens in practice
 * because goal is always >= 1).
 */
export function PledgeCalculation_perUnitFromTotal(total, denominator) {
    if (compare(denominator, fromParts(0, 0, 0, false, 0)) <= 0) {
        return fromParts(0, 0, 0, false, 0);
    }
    else {
        return PledgeCalculation_floorTo(4, op_Division(total, denominator));
    }
}

/**
 * Re-derive total from a per-unit value for display. Returns 2dp.
 */
export function PledgeCalculation_totalFromPerUnit(perUnit, denominator) {
    return PledgeCalculation_floorTo(2, op_Multiply(perUnit, denominator));
}

export const PledgeValidation_MinimumPledgeTotal = fromParts(500, 0, 0, false, 2);

/**
 * Validate that amountPerUnit * goalUnits >= $5.00
 */
export function PledgeValidation_validateMinimumPledge(amountPerUnit, goalUnits) {
    const total = PledgeCalculation_floorTo(2, op_Multiply(amountPerUnit, goalUnits));
    if (compare(total, PledgeValidation_MinimumPledgeTotal) >= 0) {
        return new FSharpResult$2(0, [undefined]);
    }
    else {
        return new FSharpResult$2(1, [toText(printf("Total pledge amount ($%.2f) must be at least $%.2f"))(total)(PledgeValidation_MinimumPledgeTotal)]);
    }
}

/**
 * Minimum check for the two-box form. The rule is always amountPerUnit * goalUnits, the
 * check the API enforces. When the Total box uses a different denominator (uncapped, past
 * goal), the message says "at goal" so it names the figure checked, not the box's figure.
 */
export function PledgeValidation_validateMinimumPledgeForTotalBox(amountPerUnit, goalUnits, totalBoxDenominator) {
    const matchValue = PledgeValidation_validateMinimumPledge(amountPerUnit, goalUnits);
    let matchResult, result;
    if (matchValue.tag === 1) {
        if (!equals(totalBoxDenominator, goalUnits)) {
            matchResult = 0;
        }
        else {
            matchResult = 1;
            result = matchValue;
        }
    }
    else {
        matchResult = 1;
        result = matchValue;
    }
    switch (matchResult) {
        case 0: {
            const totalAtGoal = PledgeCalculation_floorTo(2, op_Multiply(amountPerUnit, goalUnits));
            return new FSharpResult$2(1, [toText(printf("Total pledge amount at goal ($%.2f) must be at least $%.2f"))(totalAtGoal)(PledgeValidation_MinimumPledgeTotal)]);
        }
        default:
            return result;
    }
}

export const PledgeValidation_MaxPageSize = 100;

/**
 * Validate page and pageSize for paginated endpoints (page >= 1, 1 <= pageSize <= MaxPageSize)
 */
export function PledgeValidation_validatePagination(page, pageSize) {
    if (page < 1) {
        return new FSharpResult$2(1, ["Page must be 1 or greater"]);
    }
    else if (pageSize < 1) {
        return new FSharpResult$2(1, ["PageSize must be 1 or greater"]);
    }
    else if (pageSize > PledgeValidation_MaxPageSize) {
        return new FSharpResult$2(1, [`PageSize must be ${PledgeValidation_MaxPageSize} or less`]);
    }
    else {
        return new FSharpResult$2(0, [undefined]);
    }
}

/**
 * Validate cancel reason length (max 500 chars)
 */
export function PledgeValidation_validateCancelReason(reason) {
    let r;
    if (reason != null) {
        if ((r = reason, r.length <= 500)) {
            const r_1 = reason;
            return new FSharpResult$2(0, [r_1]);
        }
        else {
            return new FSharpResult$2(1, ["Cancel reason must be 500 characters or less"]);
        }
    }
    else {
        return new FSharpResult$2(0, [undefined]);
    }
}

//# sourceMappingURL=Pledge.js.map
